import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { StepperService } from '../../../services/stepper.service';
import {
  FacultyResponse,
  UniversityCareersResponse
} from '../../initial-form/initialForm.models';

@Component({
  selector: 'app-step-career-preferences',
  templateUrl: './step-career-preferences.component.html',
  styleUrls: ['./step-career-preferences.component.scss']
})
export class StepCareerPreferencesComponent implements OnInit {
  @Input() form!: FormGroup;

  allUniversityCareers: UniversityCareersResponse[] = [];
  allFaculties: FacultyResponse[] = [];

  allFacultyOptions: { value: string; label: string }[] = []; z
  facultyOptions: { value: string; label: string }[] = [];    

  universityCareerOptions: { value: string; label: string }[] = [];

  isLoading = false;

  constructor(private stepperService: StepperService) { }

  ngOnInit(): void {
    this.loadData().then(() => this.listenFacultySelection());
  }

  private loadData(): Promise<void> {
    this.isLoading = true;

    return Promise.all([this.loadUniversityCareers(), this.loadFaculties()])
      .then(() => {
        this.isLoading = false;
        this.updateFacultyOptions(); 
        this.filterCareersBySelectedFaculties();
      })
      .catch((error) => {
        console.error('Error loading data:', error);
        this.isLoading = false;
      });
  }

  private loadUniversityCareers(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.stepperService.loadUniversityCareers().subscribe(
        (careers) => {
          this.allUniversityCareers = careers;
          resolve();
        },
        (error) => reject(error)
      );
    });
  }

  private loadFaculties(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.stepperService.loadFaculties().subscribe(
        (faculties) => {
          this.allFaculties = faculties;
          this.allFacultyOptions = this.mapToDropdownOptions(faculties); 
          resolve();
        },
        (error) => reject(error)
      );
    });
  }

  private listenFacultySelection(): void {
    const mainFaculty = this.form.get('mainFacultyInterest');
    const extraFaculties = this.form.get('facultyInterests');

    if (mainFaculty) {
      mainFaculty.valueChanges.subscribe(() => {
        this.updateFacultyOptions();
        this.filterCareersBySelectedFaculties();
      });
    }

    if (extraFaculties) {
      extraFaculties.valueChanges.subscribe(() => {
        this.filterCareersBySelectedFaculties();
      });
    }
  }

  private updateFacultyOptions(): void {
    const selectedMainFaculty = this.form.get('mainFacultyInterest')?.value;

    this.facultyOptions = this.allFaculties
      .filter(faculty => faculty._id !== selectedMainFaculty)
      .map(f => ({ value: f._id, label: f.name }));

    // Clear selected extras if they are the same as the main faculty
    const extrasControl = this.form.get('facultyInterests');
    const currentExtras: string[] = extrasControl?.value || [];

    const filteredExtras = currentExtras.filter(f => f !== selectedMainFaculty);
    if (filteredExtras.length !== currentExtras.length) {
      extrasControl?.setValue(filteredExtras);
    }
  }

  private filterCareersBySelectedFaculties(): void {
    const selectedFacultyIds = this.getSelectedFacultyIds();

    const filteredCareers = this.allUniversityCareers.filter(career =>
      selectedFacultyIds.includes(career.faculty._id)
    );

    this.universityCareerOptions = this.mapToDropdownOptions(filteredCareers);

    const currentCareer = this.form.get('desiredCareer')?.value;
    if (currentCareer && !filteredCareers.some(c => c._id === currentCareer)) {
      this.form.get('desiredCareer')?.reset();
    }
  }

  private getSelectedFacultyIds(): string[] {
    const main = this.form.get('mainFacultyInterest')?.value;
    const extras = this.form.get('facultyInterests')?.value || [];
    const all = [main, ...extras];
    return [...new Set(all.filter(Boolean))];
  }

  private mapToDropdownOptions(items: { _id: string; name: string }[]): { value: string; label: string }[] {
    return items.map(item => ({ value: item._id, label: item.name }));
  }
}
